import React, { useState, useEffect, useRef } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const STATUS_OPTS = [
    { id: "open", label: "Open", color: "#FFB703", bg: "#FFF6E5" },
    { id: "in_progress", label: "In Progress", color: "#002FA7", bg: "#E5EBFF" },
    { id: "done", label: "Done", color: "#2A9D8F", bg: "#E0F4F1" },
];

export default function GanttView({ tasks, partById, userById, isOverdue, onEdit, onUpdateDates, onUpdateTitle, isExecutor, onReorderRows, onAddTaskForPart }) {
    // Drag state — declared at TOP so hook rules are respected
    const [drag, setDrag] = React.useState(null);
    const ganttRef = React.useRef(null);
    const [exporting, setExporting] = React.useState(false);
    const [rowDragKey, setRowDragKey] = React.useState(null);
    const [rowDragOver, setRowDragOver] = React.useState(null);
    // Inline title edit
    const [titleEditId, setTitleEditId] = React.useState(null);
    const [titleDraft, setTitleDraft] = React.useState("");

    const exportGantt = async (kind) => {
        if (!ganttRef.current) return;
        setExporting(true);
        try {
            const { toPng } = await import("html-to-image");
            const node = ganttRef.current;
            const dataUrl = await toPng(node, { pixelRatio: 2, cacheBust: true, backgroundColor: "#ffffff", width: node.scrollWidth, height: node.scrollHeight, style: { width: `${node.scrollWidth}px`, height: `${node.scrollHeight}px` } });
            if (kind === "pdf") {
                const w = window.open("");
                if (!w) { toast.error("Pop-up blocked — allow it to export PDF"); return; }
                w.document.write(`<html><head><title>Gantt</title><style>@page{size:landscape;margin:8mm} body{margin:0} img{width:100%}</style></head><body><img src="${dataUrl}"/></body></html>`);
                w.document.close();
                setTimeout(() => w.print(), 400);
            } else {
                const a = document.createElement("a");
                a.href = dataUrl; a.download = `gantt_${new Date().toISOString().slice(0, 10)}.png`; a.click();
            }
        } catch (e) {
            console.warn("Gantt export failed:", e);
            toast.error("Export failed");
        } finally { setExporting(false); }
    };

    const todayStr = new Date().toISOString().slice(0, 10);
    const valid = tasks.filter((t) => t.start_date && t.due_date);

    const DAY = 86400000;
    const PX_PER_DAY = 28;
    const LEFT_COL = 240;
    const ROW_H = 36;

    React.useEffect(() => {
        if (!drag) return;
        // Dependency-drag mode: render a follow-cursor line + on mouse-up over a
        // task bar, set that target task's depends_on to the source task.
        if (drag.mode === "dep") {
            const onMove = (e) => {
                setDrag((d) => d ? { ...d, _x: e.clientX, _y: e.clientY } : d);
            };
            const onUp = async (e) => {
                const el = document.elementFromPoint(e.clientX, e.clientY);
                const bar = el && el.closest("[data-testid^='gantt-bar-']");
                if (bar) {
                    const targetId = bar.getAttribute("data-testid").replace("gantt-bar-", "");
                    const target = tasks.find((x) => x.task_id === targetId);
                    if (target && targetId !== drag.taskId) {
                        // Avoid duplicate / cycle (target already depends on src? skip)
                        const next = Array.from(new Set([drag.taskId, ...(target.depends_on || [])]));
                        try {
                            await api.put(`/tasks/${target.task_id}`, { ...target, depends_on: next, reschedule_reason: "" });
                            toast.success(`Linked: "${target.title}" now depends on "${tasks.find((x) => x.task_id === drag.taskId)?.title || ""}"`);
                            await onUpdateDates(target, { start_date: target.start_date, due_date: target.due_date }); // triggers parent reload
                        } catch { toast.error("Link failed"); }
                    }
                }
                setDrag(null);
            };
            window.addEventListener("mousemove", onMove);
            window.addEventListener("mouseup", onUp);
            return () => { window.removeEventListener("mousemove", onMove); window.removeEventListener("mouseup", onUp); };
        }
        const onMove = (e) => {
            const dx = e.clientX - drag.startX;
            const dDays = Math.round(dx / PX_PER_DAY);
            if (dDays === 0) return;
            const t = tasks.find((x) => x.task_id === drag.taskId);
            if (!t) return;
            const isoOf = (d) => d.toISOString().slice(0, 10);
            const shift = (iso, days) => isoOf(new Date(new Date(iso).getTime() + days * DAY));
            let newStart = drag.originStart, newDue = drag.originDue;
            if (drag.mode === "move") { newStart = shift(drag.originStart, dDays); newDue = shift(drag.originDue, dDays); }
            else if (drag.mode === "left") { newStart = shift(drag.originStart, dDays); if (newStart > newDue) newStart = newDue; }
            else if (drag.mode === "right") { newDue = shift(drag.originDue, dDays); if (newDue < newStart) newDue = newStart; }
            t._dragStart = newStart; t._dragDue = newDue;
            setDrag({ ...drag, _tick: Math.random() });
        };
        const onUp = async () => {
            const t = tasks.find((x) => x.task_id === drag.taskId);
            if (t && (t._dragStart || t._dragDue)) {
                await onUpdateDates(t, { start_date: t._dragStart || t.start_date, due_date: t._dragDue || t.due_date });
                delete t._dragStart; delete t._dragDue;
            }
            setDrag(null);
        };
        window.addEventListener("mousemove", onMove);
        window.addEventListener("mouseup", onUp);
        return () => { window.removeEventListener("mousemove", onMove); window.removeEventListener("mouseup", onUp); };
    }, [drag, tasks, onUpdateDates]);  // eslint-disable-line react-hooks/exhaustive-deps

    if (!valid.length) return <div className="text-zinc-400 italic py-12 text-center">No tasks with both planned dates. Add dates to see them on the Gantt.</div>;

    // Date range: pad ±3 days
    let minD = valid.reduce((m, t) => t.start_date < m ? t.start_date : m, valid[0].start_date);
    let maxD = valid.reduce((m, t) => t.due_date > m ? t.due_date : m, valid[0].due_date);
    const minDate = new Date(new Date(minD).getTime() - 3 * DAY);
    const maxDate = new Date(new Date(maxD).getTime() + 3 * DAY);
    const totalDays = Math.max(1, Math.round((maxDate - minDate) / DAY) + 1);
    const isoOf = (d) => d.toISOString().slice(0, 10);

    // Build day cells + month header spans
    const dayCells = [];
    const monthSpans = [];
    let curMonth = "";
    let curSpan = 0;
    for (let i = 0; i < totalDays; i++) {
        const d = new Date(minDate.getTime() + i * DAY);
        dayCells.push(d);
        const lbl = d.toLocaleString("en-US", { month: "short", year: "numeric" });
        if (lbl !== curMonth) {
            if (curSpan) monthSpans.push({ label: curMonth, span: curSpan });
            curMonth = lbl; curSpan = 1;
        } else curSpan += 1;
    }
    if (curSpan) monthSpans.push({ label: curMonth, span: curSpan });

    // Group by part_id
    const groups = {};
    valid.forEach((t) => {
        const k = t.part_id || "__no_part__";
        if (!groups[k]) groups[k] = [];
        groups[k].push(t);
    });
    Object.keys(groups).forEach((k) => groups[k].sort((a, b) => {
        const pa = a.parent_task_id ? 1 : 0; const pb = b.parent_task_id ? 1 : 0;
        if (pa !== pb) return pa - pb;
        const sa = (a.sort_order ?? 9999); const sb = (b.sort_order ?? 9999);
        if (sa !== sb) return sa - sb;
        return (a.start_date || "").localeCompare(b.start_date || "");
    }));

    // Build flat row list with row index for SVG dependency lines
    const flatRows = [];
    Object.entries(groups).forEach(([partId, items]) => {
        flatRows.push({ _isHeader: true, partId, count: items.length });
        items.forEach((t) => flatRows.push(t));
    });
    const rowIndexByTaskId = {};
    flatRows.forEach((r, i) => { if (!r._isHeader) rowIndexByTaskId[r.task_id] = i; });

    const offsetDays = (iso) => Math.round((new Date(iso) - minDate) / DAY);
    const todayOffset = offsetDays(todayStr);

    const onMouseDown = (e, t, mode) => {
        e.preventDefault(); e.stopPropagation();
        setDrag({ taskId: t.task_id, mode, startX: e.clientX, originStart: t.start_date, originDue: t.due_date });
    };

    const onDependencyDragStart = (e, t) => {
        e.preventDefault(); e.stopPropagation();
        const rect = e.currentTarget.getBoundingClientRect();
        setDrag({ taskId: t.task_id, mode: "dep", startX: e.clientX, _x: e.clientX, _y: e.clientY, _srcX: rect.left + rect.width / 2, _srcY: rect.top + rect.height / 2 });
    };

    const getDates = (t) => ({ start: t._dragStart || t.start_date, due: t._dragDue || t.due_date });
    const svgHeight = flatRows.length * ROW_H + 8;
    const svgWidth = totalDays * PX_PER_DAY;

    return (
        <div data-testid="tasks-gantt-view">
            {drag?.mode === "dep" && drag._srcX != null && drag._x != null && (
                <svg style={{ position: "fixed", inset: 0, pointerEvents: "none", zIndex: 50 }}>
                    <line x1={drag._srcX} y1={drag._srcY} x2={drag._x} y2={drag._y} stroke="#002FA7" strokeWidth="2" strokeDasharray="6 4" />
                    <circle cx={drag._x} cy={drag._y} r="4" fill="#002FA7" />
                </svg>
            )}
            <div className="flex items-center gap-2 mb-2">
                <Button variant="outline" className="rounded-none h-8 text-xs" disabled={exporting} onClick={() => exportGantt("png")} data-testid="gantt-export-png">
                    {exporting ? "Exporting…" : "Download PNG"}
                </Button>
                <Button variant="outline" className="rounded-none h-8 text-xs" disabled={exporting} onClick={() => exportGantt("pdf")} data-testid="gantt-export-pdf">
                    {exporting ? "Exporting…" : "Export PDF (print)"}
                </Button>
            </div>
        <div ref={ganttRef} className="border border-zinc-200 overflow-x-auto">
            {/* Month header row */}
            <div className="flex border-b border-zinc-300 bg-zinc-50 sticky top-0 z-20">
                <div className="font-mono text-[11px] uppercase tracking-wider text-zinc-500 flex items-center px-3 border-r border-zinc-300" style={{ width: LEFT_COL, minWidth: LEFT_COL }}>Task / Assignee</div>
                <div style={{ width: svgWidth, minWidth: svgWidth, display: "flex" }}>
                    {monthSpans.map((m, i) => (
                        <div key={i} className="font-mono text-xs font-semibold text-zinc-700 text-center border-r border-zinc-200 py-1" style={{ width: m.span * PX_PER_DAY }}>
                            {m.label}
                        </div>
                    ))}
                </div>
            </div>
            {/* Day numbers */}
            <div className="flex border-b border-zinc-200 bg-zinc-50/60 sticky z-10" style={{ top: 28 }}>
                <div style={{ width: LEFT_COL, minWidth: LEFT_COL }} className="border-r border-zinc-300" />
                <div style={{ width: svgWidth, minWidth: svgWidth, display: "flex" }}>
                    {dayCells.map((d, i) => {
                        const dow = d.getDay();
                        const isWk = dow === 0 || dow === 6;
                        const isToday = isoOf(d) === todayStr;
                        return (
                            <div key={i}
                                className={`font-mono text-[10px] text-center py-1 border-r border-zinc-100 ${isToday ? "bg-[#FEE2E2] text-[#E63946] font-bold" : (isWk ? "bg-zinc-100 text-zinc-400" : "text-zinc-500")}`}
                                style={{ width: PX_PER_DAY }}>
                                {d.getDate()}
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Body — left task column + right scrollable grid */}
            <div className="relative">
                {/* Weekend + today background stripes (in the right side only) */}
                <div className="absolute pointer-events-none" style={{ left: LEFT_COL, top: 0, width: svgWidth, height: svgHeight }}>
                    {dayCells.map((d, i) => {
                        const dow = d.getDay();
                        const isWk = dow === 0 || dow === 6;
                        if (!isWk) return null;
                        return <div key={i} style={{ position: "absolute", left: i * PX_PER_DAY, top: 0, width: PX_PER_DAY, height: "100%", background: "#fafafa" }} />;
                    })}
                    {todayOffset >= 0 && todayOffset < totalDays && (
                        <div style={{ position: "absolute", left: todayOffset * PX_PER_DAY + PX_PER_DAY / 2, top: 0, width: 2, height: "100%", background: "#E63946", opacity: 0.5 }} />
                    )}
                </div>

                {/* SVG dependency arrows */}
                <svg className="absolute pointer-events-none" style={{ left: LEFT_COL, top: 0 }} width={svgWidth} height={svgHeight}>
                    {flatRows.map((row, ri) => {
                        if (row._isHeader || !row.depends_on?.length) return null;
                        return row.depends_on.map((depId) => {
                            const fromRow = rowIndexByTaskId[depId];
                            if (fromRow === undefined) return null;
                            const fromTask = flatRows[fromRow];
                            const fromDates = getDates(fromTask);
                            const toDates = getDates(row);
                            const x1 = offsetDays(fromDates.due) * PX_PER_DAY + PX_PER_DAY;
                            const y1 = fromRow * ROW_H + ROW_H / 2;
                            const x2 = offsetDays(toDates.start) * PX_PER_DAY;
                            const y2 = ri * ROW_H + ROW_H / 2;
                            return (
                                <g key={depId}>
                                    <path d={`M${x1},${y1} L${x1 + 8},${y1} L${x1 + 8},${y2} L${x2 - 4},${y2}`} stroke="#71717a" strokeWidth="1.5" fill="none" />
                                    <polygon points={`${x2 - 6},${y2 - 4} ${x2},${y2} ${x2 - 6},${y2 + 4}`} fill="#71717a" />
                                </g>
                            );
                        });
                    })}
                </svg>

                {/* Rows */}
                {flatRows.map((row, ri) => {
                    if (row._isHeader) {
                        return (
                            <div key={`h-${ri}`} className="flex items-center bg-[#FFF6E5] border-b border-zinc-200" style={{ height: ROW_H }}>
                                <div className="font-mono text-xs uppercase tracking-wider text-zinc-700 px-3 flex items-center gap-2" style={{ width: LEFT_COL }}>
                                    <span className="font-semibold">{row.partId === "__no_part__" ? "(No part)" : (partById[row.partId]?.part_name || row.partId)}</span>
                                    <span className="text-zinc-400">{row.count} task{row.count > 1 ? "s" : ""}</span>
                                    {!isExecutor && row.partId !== "__no_part__" && onAddTaskForPart && (
                                        <button
                                            onClick={() => onAddTaskForPart(row.partId)}
                                            className="ml-auto text-[#002FA7] hover:underline text-[11px] font-mono"
                                            title="Add task to this part"
                                            data-testid={`gantt-add-task-${row.partId}`}
                                        >+ task</button>
                                    )}
                                </div>
                                <div className="flex-1" />
                            </div>
                        );
                    }
                    const t = row;
                    const dates = getDates(t);
                    const startOff = offsetDays(dates.start);
                    const endOff = offsetDays(dates.due);
                    const left = startOff * PX_PER_DAY;
                    const width = Math.max(PX_PER_DAY, (endOff - startOff + 1) * PX_PER_DAY);
                    const duration = endOff - startOff + 1;
                    const overdue = isOverdue(t);
                    const statusMeta = STATUS_OPTS.find((s) => s.id === t.status) || STATUS_OPTS[0];
                    const barColor = t.status === "done" ? "#2A9D8F" : (overdue ? "#E63946" : (t.status === "in_progress" ? "#002FA7" : "#FFB703"));
                    const assignee = t.assigned_to ? (userById[t.assigned_to]?.name || userById[t.assigned_to]?.email || "?") : "—";
                    const initials = assignee === "—" ? "?" : assignee.split(/\s+/).map((w) => w[0]).join("").slice(0, 2).toUpperCase();
                    return (
                        <div
                            key={t.task_id}
                            className={`flex border-b border-zinc-100 hover:bg-zinc-50 ${rowDragOver === t.task_id ? "bg-blue-50" : ""}`}
                            style={{ height: ROW_H }}
                            draggable={!isExecutor && rowDragKey === t.task_id}
                            onDragStart={(e) => { e.dataTransfer.setData("text/plain", t.task_id); e.dataTransfer.effectAllowed = "move"; }}
                            onDragEnd={() => { setRowDragKey(null); setRowDragOver(null); }}
                            onDragOver={(e) => { if (!isExecutor) { e.preventDefault(); e.dataTransfer.dropEffect = "move"; setRowDragOver(t.task_id); } }}
                            onDragLeave={() => setRowDragOver((prev) => prev === t.task_id ? null : prev)}
                            onDrop={(e) => {
                                e.preventDefault();
                                setRowDragOver(null);
                                const srcId = e.dataTransfer.getData("text/plain");
                                if (srcId && srcId !== t.task_id) onReorderRows(srcId, t.task_id);
                            }}
                            data-testid={`gantt-row-${t.task_id}`}
                        >
                            <div className="px-3 flex items-center gap-2 border-r border-zinc-100" style={{ width: LEFT_COL, paddingLeft: 12 + (t.parent_task_id ? 18 : 0) }}>
                                {!isExecutor && (
                                    <span
                                        className="cursor-grab text-zinc-400 hover:text-[#002FA7] select-none px-1"
                                        title="Drag to reorder within this part group"
                                        onMouseDown={() => setRowDragKey(t.task_id)}
                                        onMouseUp={() => setRowDragKey(null)}
                                        data-testid={`gantt-drag-handle-${t.task_id}`}
                                    >⋮⋮</span>
                                )}
                                {t.parent_task_id ? <span className="text-zinc-300">↳</span> : null}
                                <span title={assignee} className="inline-flex items-center justify-center w-6 h-6 rounded-full text-[9px] font-mono text-white" style={{ background: barColor }}>{initials}</span>
                                {titleEditId === t.task_id ? (
                                    <input
                                        autoFocus
                                        value={titleDraft}
                                        onChange={(e) => setTitleDraft(e.target.value)}
                                        onBlur={() => {
                                            const next = (titleDraft || "").trim();
                                            if (next && next !== t.title) onUpdateTitle && onUpdateTitle(t, next);
                                            setTitleEditId(null);
                                        }}
                                        onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); if (e.key === "Escape") { setTitleDraft(t.title); setTitleEditId(null); } }}
                                        className="border border-[#002FA7] rounded-none h-6 text-xs px-1 flex-1 font-mono"
                                        data-testid={`gantt-title-input-${t.task_id}`}
                                    />
                                ) : (
                                    <span
                                        className="truncate text-xs flex-1 cursor-text hover:bg-zinc-100 px-1"
                                        title={isExecutor ? t.title : "Double-click to rename"}
                                        onDoubleClick={() => { if (isExecutor) return; setTitleDraft(t.title); setTitleEditId(t.task_id); }}
                                        data-testid={`gantt-title-${t.task_id}`}
                                    >{t.title}</span>
                                )}
                                {overdue && <span className="text-[9px] uppercase text-[#E63946] font-mono">!</span>}
                            </div>
                            <div className="relative" style={{ width: svgWidth }}>
                                <div
                                    onClick={(e) => { if (!drag) onEdit(t); }}
                                    onMouseDown={(e) => onMouseDown(e, t, "move")}
                                    className="absolute select-none shadow-sm flex items-center justify-between group"
                                    style={{
                                        left, top: 6, height: ROW_H - 12, width,
                                        background: barColor,
                                        color: "#fff",
                                        borderRadius: 4,
                                        cursor: drag?.taskId === t.task_id ? "grabbing" : "grab",
                                        opacity: t.status === "done" ? 0.8 : 1,
                                        boxShadow: drag?.taskId === t.task_id ? "0 0 0 2px #002FA7" : "",
                                    }}
                                    title={`${dates.start} → ${dates.due}  (${duration}d) · drag body to move · drag edges to resize · drag the link icon to create a dependency`}
                                    data-testid={`gantt-bar-${t.task_id}`}
                                >
                                    {/* resize handle left — wider + visible affordance */}
                                    <div
                                        onMouseDown={(e) => { e.stopPropagation(); onMouseDown(e, t, "left"); }}
                                        style={{ width: 10, height: "100%", cursor: "ew-resize", background: "rgba(255,255,255,0.12)" }}
                                        className="opacity-60 group-hover:opacity-100"
                                        title="Drag to change start date"
                                    />
                                    {width >= 60 ? (
                                        <span className="text-[10px] font-mono px-1 truncate flex-1 text-center">{duration}d</span>
                                    ) : <span className="flex-1" />}
                                    {/* dependency anchor — drag from here onto another bar to create a depends_on link */}
                                    <div
                                        onMouseDown={(e) => { e.stopPropagation(); onDependencyDragStart(e, t); }}
                                        style={{ width: 12, height: "100%", cursor: "crosshair", background: "rgba(255,255,255,0.18)", display: "flex", alignItems: "center", justifyContent: "center" }}
                                        className="opacity-60 group-hover:opacity-100"
                                        title="Drag from here to another task to link a dependency"
                                        data-testid={`gantt-dep-handle-${t.task_id}`}
                                    >
                                        <span className="text-[10px] leading-none">⇢</span>
                                    </div>
                                    {/* resize handle right */}
                                    <div
                                        onMouseDown={(e) => { e.stopPropagation(); onMouseDown(e, t, "right"); }}
                                        style={{ width: 10, height: "100%", cursor: "ew-resize", background: "rgba(255,255,255,0.12)" }}
                                        className="opacity-60 group-hover:opacity-100"
                                        title="Drag to change due date"
                                    />
                                </div>
                                {/* tiny actual-end dot */}
                                {t.actual_end && (
                                    <div title={`Actual end: ${t.actual_end}`} className="absolute" style={{ left: offsetDays(t.actual_end) * PX_PER_DAY + PX_PER_DAY / 2 - 4, top: ROW_H / 2 - 4, width: 8, height: 8, borderRadius: 4, background: "#2A9D8F", border: "2px solid white" }} />
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
            <div className="px-3 py-2 text-[10px] text-zinc-500 font-mono flex items-center gap-4 border-t border-zinc-200 bg-zinc-50">
                <span><b>Drag</b> the bar to move · <b>drag edges</b> to resize · <b>click</b> to edit</span>
                <span className="ml-auto inline-flex items-center gap-3">
                    <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded-full" style={{ background: "#FFB703" }} /> Open</span>
                    <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded-full" style={{ background: "#002FA7" }} /> Doing</span>
                    <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded-full" style={{ background: "#2A9D8F" }} /> Done</span>
                    <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded-full" style={{ background: "#E63946" }} /> Overdue</span>
                    <span className="inline-flex items-center gap-1"><span className="w-px h-3" style={{ background: "#E63946" }} /> Today</span>
                </span>
            </div>
        </div>
        </div>
    );
}
